import type { CustomVariableDef } from "@talentkontor/domain";
import type { Api } from "../../../data/api";

/**
 * Bibliothek eigener Vertragsangaben je Agentur (PARITAET-18, AD-128): Definitionen wie die eigenen Angaben einer Vorlage,
 * einmal gepflegt (org_settings.contract_variable_library, 0087) und in jede Vorlage übernommen. Die Vorlage bekommt eine
 * Kopie; spätere Änderungen an der Bibliothek ändern keine fertige Vorlage (Fassungen bleiben nachvollziehbar).
 */

export function libraryOf(raw: unknown): CustomVariableDef[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter((d): d is CustomVariableDef => !!d && typeof d === "object" && typeof (d as { key?: unknown }).key === "string"
    && typeof (d as { label?: { de?: unknown } }).label?.de === "string");
}

/** Angabe in die Bibliothek: gleicher Schlüssel ersetzt den Eintrag, sonst hinten anfügen (höchstens 100) */
export function libraryUpsert(lib: readonly CustomVariableDef[], def: CustomVariableDef): CustomVariableDef[] {
  const clean: CustomVariableDef = { ...def, label: { de: def.label.de.trim(), en: def.label.en.trim() } };
  const i = lib.findIndex((d) => d.key === def.key);
  if (i >= 0) return lib.map((d, k) => (k === i ? clean : d));
  return [...lib, clean].slice(0, 100);
}

export function libraryRemove(lib: readonly CustomVariableDef[], key: string): CustomVariableDef[] {
  return lib.filter((d) => d.key !== key);
}

/** Einträge, die die Vorlage noch nicht hat (gleicher Schlüssel = schon übernommen) */
export function libraryAdoptable(lib: readonly CustomVariableDef[], defs: readonly CustomVariableDef[]): CustomVariableDef[] {
  const have = new Set(defs.map((d) => d.key));
  return lib.filter((d) => !have.has(d.key));
}

/** Kopie für die Vorlage: eigenständig, ohne Verweis auf die Bibliothek */
export function adoptFromLibrary(defs: readonly CustomVariableDef[], entry: CustomVariableDef): CustomVariableDef[] {
  if (defs.some((d) => d.key === entry.key)) return [...defs];
  return [...defs, JSON.parse(JSON.stringify(entry)) as CustomVariableDef];
}

export async function loadLibrary(api: Api, orgId: string): Promise<CustomVariableDef[]> {
  const rows = await api.select<{ contract_variable_library: unknown }>("org_settings", { columns: "contract_variable_library", filters: [["org_id", "eq", orgId]] })
    .catch(() => [] as { contract_variable_library: unknown }[]);
  return libraryOf(rows[0]?.contract_variable_library);
}

export async function saveLibrary(api: Api, orgId: string, lib: readonly CustomVariableDef[]): Promise<void> {
  await api.update("org_settings", [["org_id", "eq", orgId]], { contract_variable_library: lib });
}
